import { useState, useEffect } from "react";
import axios from "axios";
import { Apiurl } from "../../../shared/services/apirest";
import { getAuthHeader } from "../utils/investigadorUtils";
import LocationService from "../../../shared/services/LocationService";

export const useRegistrarPaciente = (initialData, isEditMode, activeProjectId, activeProject, showToast, setView, setSelectedPaciente, onCancel) => {
    const [municipio, setMunicipio] = useState(initialData?.municipio || "");
    const [zona, setZona] = useState(initialData?.zona || "");

    const extractLugar = (addr) => {
        if (!addr) return "";
        const parts = addr.split(',');
        if (parts.length >= 3) {
            const sub = parts[2].split('-');
            return sub[0].trim();
        }
        return "";
    };

    const [lugar, setLugar] = useState(initialData?.lugar || extractLugar(initialData?.direccion) || "");
    const [coords, setCoords] = useState({
        lat: initialData?.latitud || "",
        lng: initialData?.longitud || ""
    });
    const [fechaNacimiento, setFechaNacimiento] = useState(initialData?.fecha_nacimiento || "");
    const [fechaEvento, setFechaEvento] = useState(initialData?.fecha_evento || new Date().toISOString().split('T')[0]);
    const [edad, setEdad] = useState(initialData?.edad || "");
    const [locationsHierarchy, setLocationsHierarchy] = useState({});
    const [municipiosList, setMunicipiosList] = useState([]);

    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const [showClassification, setShowClassification] = useState(false);
    const [createdPatient, setCreatedPatient] = useState(null);
    const [loadingClassification, setLoadingClassification] = useState(false);
    const [patientExistsInProject, setPatientExistsInProject] = useState(null);

    useEffect(() => {
        LocationService.getHierarchy().then(data => {
            setLocationsHierarchy(data);
            setMunicipiosList(Object.keys(data).sort());
        });
    }, []);

    const handleMunicipioChange = (val) => {
        setMunicipio(val);
        updateCoords(val, zona);
    };

    const handleZonaChange = (val) => {
        setZona(val);
        updateCoords(municipio, val);
    };

    const updateCoords = (mun, zn) => {
        if (locationsHierarchy[mun]) {
            let lat = "";
            let lng = "";
            const area = locationsHierarchy[mun][zn];
            if (area) {
                const place = area["Centro"] || Object.values(area)[0];
                if (place) { lat = place.lat; lng = place.lng; }
            } else {
                // Fallback if zone not found
                const urb = locationsHierarchy[mun]["Urbana"];
                if (urb) {
                    const place = urb["Centro"] || Object.values(urb)[0];
                    if (place) { lat = place.lat; lng = place.lng; }
                }
            }
            if (lat && lng) setCoords({ lat, lng });
        }
    };

    const handleFechaChange = (val) => {
        setFechaNacimiento(val);
        if (val) {
            const birthDate = new Date(val);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
            setEdad(age >= 0 ? age : 0);
        }
    };

    const handlePreSubmit = async (formDataObj) => {
        if (!activeProjectId && !isEditMode) {
            showToast("⚠️ Debe seleccionar o crear un proyecto activo antes de registrar.", "warning");
            setView("misProyectos");
            return;
        }

        if (!isEditMode) {
            try {
                const res = await axios.get(`${Apiurl}casos/pacientes/?documento=${formDataObj.documento}`, getAuthHeader());
                const matching = res.data.find(p =>
                    p.documento === formDataObj.documento &&
                    (p.proyecto === activeProjectId || p.codigo_proyecto_investigacion === activeProject?.codigo)
                );
                if (matching) {
                    setPatientExistsInProject(matching);
                    setConfirmData(formDataObj);
                    setShowConfirm(true);
                    return;
                }
            } catch (err) {
                console.error("Error checking project patient:", err);
            }
        }

        const enrichedData = {
            ...formDataObj,
            edad,
            latitud: coords.lat,
            longitud: coords.lng,
            municipio,
            fecha_evento: fechaEvento,
            proyecto: activeProjectId,
            codigo_proyecto_investigacion: activeProject?.codigo || "",
            direccion: `${municipio}, ${zona}, ${lugar} - ${formDataObj.direccion_detalles || ''}`
        };

        setPatientExistsInProject(null);
        setConfirmData(enrichedData);
        setShowConfirm(true);
    };

    const handleFinalSubmit = async () => {
        try {
            if (isEditMode && initialData.id) {
                await axios.put(`${Apiurl}casos/pacientes/${initialData.id}/`, confirmData, getAuthHeader());
                showToast("✅ Paciente actualizado correctamente.", "success");
                if (onCancel) onCancel();
            } else {
                const res = await axios.post(`${Apiurl}casos/pacientes/`, confirmData, getAuthHeader());
                showToast("✅ Paciente registrado correctamente.", "success");
                setCreatedPatient(res.data);
                setShowClassification(true);
            }
            setShowConfirm(false);
            setConfirmData(null);
        } catch (err) {
            let msg = isEditMode ? "Error al actualizar." : "Error al registrar.";
            if (err.response?.data) msg += " " + JSON.stringify(err.response.data);
            showToast(msg, "danger");
            setShowConfirm(false);
        }
    };

    const handleClassification = async (resultado) => {
        setLoadingClassification(true);
        try {
            await axios.patch(`${Apiurl}casos/pacientes/${createdPatient.id}/`, { resultado_inicial: resultado }, getAuthHeader());
            if (resultado === 'NEGATIVO') {
                showToast("✅ Resultado NEGATIVO guardado. Registro epidemiológico finalizado.", "success");
                if (onCancel) onCancel();
                else setView("home");
            } else {
                showToast("✅ Resultado POSITIVO detectado. Redirigiendo a Registro de Análisis...", "info");
                setSelectedPaciente(createdPatient);
                setView("registrarCaso");
            }
        } catch (err) {
            console.error(err);
            showToast("Error al guardar la clasificación.", "danger");
        } finally {
            setLoadingClassification(false);
        }
    };

    return {
        municipio, zona, lugar, setLugar, coords, fechaNacimiento, fechaEvento, setFechaEvento, edad, municipiosList,
        showConfirm, setShowConfirm, confirmData, showClassification, createdPatient,
        loadingClassification, patientExistsInProject, setCreatedPatient, setShowClassification,
        handleMunicipioChange, handleZonaChange, handleFechaChange, handlePreSubmit, handleFinalSubmit, handleClassification
    };
};
